import { useEffect, useEffectEvent, useState } from 'react';
import { useParams } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import useAgent from './useAgent';
import useAnalytics from './useAnalytics';
import useTimeoutValue from './useTimeoutValue';

import client from 'apollo/client';
import { ADD_AGENT_AUDIT_LOG_FOR_CUSTOMER_SSN_MUTATION } from 'mutations/addAgentAuditLog';
import { CUSTOMER_OVERVIEW } from 'queries/customer';
import { GET_FULL_SSN } from 'queries/fullSsn';
import { ANALYTICS_EVENTS } from 'utilities/constants';

const useCustomerFullSsn = () => {
  const agent = useAgent();
  const { trackEvent } = useAnalytics();
  const { customerId } = useParams();
  const [fullSsn, setFullSsn] = useState('');
  const [failureMessage, setFailureMessage] = useTimeoutValue('', 5000);

  const [addAuditLogForFullSsn, auditLogQuery] = useMutation(
    ADD_AGENT_AUDIT_LOG_FOR_CUSTOMER_SSN_MUTATION,
  );

  const [getFullSsn, fullSsnQuery] = useLazyQuery(GET_FULL_SSN, {
    fetchPolicy: 'network-only',
  });

  const retrieveFullSsn = async () => {
    try {
      const response = await getFullSsn({ variables: { customerId } });
      setFullSsn(response.data.fullSsn.fullSsn);
    } catch {
      setFailureMessage('Failed to get full SSN.');
    }
  };

  const requestFullSsn = async () => {
    if (!fullSsn) {
      try {
        trackEvent(ANALYTICS_EVENTS.FULL_SSN_REQUESTED);
        try {
          await addAuditLogForFullSsn({
            variables: {
              customerId,
              agentId: agent?.user?.requester_id,
              agentName: agent?.user?.name,
            },
          });
        } catch {
          setFailureMessage(
            'Failed to save audit log for full SSN request. unable to get full SSN.',
          );
          return;
        }

        retrieveFullSsn();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const reset = useEffectEvent(() => {
    setFullSsn('');
  });

  /**
   *  Handles two reset cases:
   *  1: customerId changes
   *  2: customer overview data has been refetched
   */
  useEffect(() => {
    if (customerId) {
      let subscription = client
        .watchQuery({
          query: CUSTOMER_OVERVIEW,
          variables: { customerId },
        })
        .subscribe(() => {
          reset();
        });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [customerId]);

  return [
    fullSsn,
    requestFullSsn,
    failureMessage,
    Object.assign({}, fullSsnQuery, auditLogQuery),
  ];
};

export default useCustomerFullSsn;
